import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ImageSquare, Info, CheckSquareOffset } from "@phosphor-icons/react";
import FarmersNav from "@/components/FarmersNav";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import { fpPath } from "@/lib/site";
import { TOOL_ARTICLE_BY_SLUG } from "@/data/toolArticles";

/**
 * FarmersToolArticle — the shared page rendered at /tools/:slug.
 *
 * Renders the evergreen introductory article for one of the twelve
 * FarmersPulse home tools. The page inherits the current geographic
 * path via FarmersNav, offers Listen & Translate on the whole
 * article, and closes with a hard CTA to the live tool plus links to
 * three related tools.
 *
 * Infographics: once the 12 infographic assets are uploaded, set
 * `infographic_url` on the entry in `data/toolArticles.js` and the
 * "coming soon" placeholder is replaced with the actual image.
 */
export default function FarmersToolArticle() {
  const { slug } = useParams();
  const article = TOOL_ARTICLE_BY_SLUG[slug];

  const listenText = useMemo(() => {
    if (!article) return "";
    const bits = [article.title, article.subtitle, ...article.intro];
    for (const s of article.sections) {
      bits.push(s.h);
      bits.push(s.body.join(". "));
    }
    bits.push(article.guidance);
    return bits.join(". ");
  }, [article]);

  if (!article) return <Navigate to={fpPath("/")} replace />;

  return (
    <main
      className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]"
      data-testid="farmers-tool-article"
      data-tool-slug={article.slug}
    >
      <div className="max-w-5xl mx-auto"><FarmersNav /></div>

      <div className="max-w-3xl mx-auto mt-6" data-translate-target="true">
        <Link
          to={fpPath("/")}
          className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]"
          data-testid="tool-article-back"
        >
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>

        <header className="mt-3" data-testid="tool-article-header">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            {article.kicker}
          </div>
          <h1
            className="mt-1 font-serif-display text-4xl sm:text-5xl leading-[1.02] tracking-tight text-[#151312]"
            data-testid="tool-article-title"
          >
            {article.title}
          </h1>
          <p className="mt-3 text-lg text-[#3A3830]" data-testid="tool-article-subtitle">
            {article.subtitle}
          </p>
        </header>

        <div className="mt-5">
          <ListenTranslateBar text={listenText} testidPrefix="tool-article-tools" />
        </div>

        <InfographicBlock article={article} />

        <section className="mt-7 space-y-4" data-testid="tool-article-intro">
          {article.intro.map((p, i) => (
            <p key={i} className="text-[17px] leading-relaxed text-[#241209]">{p}</p>
          ))}
        </section>

        {article.sections.map((s, i) => (
          <section key={i} className="mt-8" data-testid={`tool-article-section-${i + 1}`}>
            <h2 className="font-serif-display text-2xl md:text-[28px] leading-tight text-[#151312]">
              {s.h}
            </h2>
            <ul className="mt-3 space-y-2">
              {s.body.map((line, j) => (
                <li key={j} className="flex gap-2 text-[16px] leading-relaxed text-[#241209]">
                  <CheckSquareOffset size={18} weight="fill" className="mt-1 flex-none text-[#2D8659]" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}

        <aside
          className="mt-10 rounded-2xl border-l-4 border-[color:var(--np-brand,#B85F2A)] bg-white p-5"
          data-testid="tool-article-guidance"
        >
          <div className="flex items-start gap-3">
            <Info size={20} weight="fill" className="mt-0.5 flex-none text-[color:var(--np-brand,#B85F2A)]" />
            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                Important guidance
              </div>
              <p className="mt-1 text-[15px] leading-relaxed text-[#241209]">{article.guidance}</p>
            </div>
          </div>
        </aside>

        <section
          className="mt-10 rounded-3xl bg-[#0F3D2A] text-[#F5EBDC] p-6 md:p-8"
          data-testid="tool-article-cta"
        >
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#F9C784]">
            Page call to action
          </div>
          <h3 className="mt-1 font-serif-display text-2xl md:text-3xl leading-tight">
            Ready to use the tool?
          </h3>
          <Link
            to={article.cta_to}
            data-testid="tool-article-cta-btn"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[color:var(--np-brand,#B85F2A)] px-5 py-2.5 text-white font-mono-tech text-[11px] tracking-[0.28em] uppercase hover:opacity-95"
          >
            {article.cta_label} <ArrowRight size={12} weight="bold" />
          </Link>
        </section>

        <section className="mt-10" data-testid="tool-article-related">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Related FarmersPulse pages
          </div>
          <div className="mt-3 grid sm:grid-cols-3 gap-3">
            {article.related.map((rslug) => {
              const r = TOOL_ARTICLE_BY_SLUG[rslug];
              if (!r) return null;
              return (
                <Link
                  key={rslug}
                  to={fpPath(`/tools/${rslug}`)}
                  data-testid={`tool-article-related-${rslug}`}
                  className="rounded-2xl border border-black/10 bg-white p-4 hover:border-[#2D8659] hover:shadow-[0_8px_24px_-16px_rgba(15,61,42,0.35)] transition"
                >
                  <div className="font-mono-tech text-[9px] tracking-[0.3em] uppercase text-[#8A857A]">
                    {r.kicker.replace(/^Article \d+\s·\s/, "")}
                  </div>
                  <div className="mt-1 font-serif-display text-lg leading-tight text-[#151312]">
                    {r.title}
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                    Read <ArrowRight size={10} weight="bold" />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        <p
          className="mt-10 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#8A857A]"
          data-testid="tool-article-modules"
        >
          Suggested page modules · {article.modules}
        </p>
      </div>
    </main>
  );
}

function InfographicBlock({ article }) {
  if (article.infographic_url) {
    return (
      <figure className="mt-6 rounded-3xl overflow-hidden border border-black/10 bg-white" data-testid="tool-article-infographic">
        <img
          src={article.infographic_url}
          alt={article.infographic_alt || `${article.title} infographic`}
          className="w-full h-auto"
          loading="lazy"
        />
      </figure>
    );
  }
  return (
    <div
      className="mt-6 rounded-3xl border border-dashed border-[#0F3D2A]/25 bg-white/60 p-6 flex items-start gap-3"
      data-testid="tool-article-infographic-placeholder"
    >
      <ImageSquare size={26} weight="duotone" className="mt-1 flex-none text-[#2D8659]" />
      <div>
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#2D8659]">
          Infographic coming soon
        </div>
        <p className="mt-1 text-[14px] text-[#3A3830]">
          A visual summary for <strong>{article.title}</strong> will appear here once the FarmersPulse
          editorial team uploads it.
        </p>
      </div>
    </div>
  );
}
